import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../hooks/useLanguage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVATAR_URL = "https://customer-assets.emergentagent.com/job_legal-consultations/artifacts/4wbcd0oh_ChatGPT%20Image%20Mar%203%2C%202026%2C%2011_47_52%20AM.png";

const AIChatWidget = () => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Initialize greeting message and reset session when language changes
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: t('chat.greeting')
    }]);
    // Reset session when language changes to get fresh responses in new language
    setSessionId(null);
  }, [language, t]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/chat`, {
        message: userMessage,
        session_id: sessionId,
        language: language  // Send current language to backend
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response 
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: t('chat.errorMessage')
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="chat-widget-container" data-testid="ai-chat-widget">
      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window" data-testid="chat-window">
          {/* Header */}
          <div className="chat-header" style={{ background: 'linear-gradient(135deg, #1F2A44 0%, #151D30 100%)' }}>
            <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-[#C2A46D]">
              <img 
                src={AVATAR_URL} 
                alt="AI Assistant" 
                className="w-full h-full object-cover object-top scale-150"
              />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">{t('chat.title')}</h3>
              <p className="text-xs opacity-80">{t('chat.subtitle')}</p>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="close-chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div 
                key={index} 
                className={`chat-message ${msg.role}`}
                style={msg.role === 'user' ? { background: '#1F2A44' } : {}}
                data-testid={`chat-message-${index}`}
              >
                {msg.content}
              </div>
            ))}
            {isLoading && (
              <div className="typing-indicator">
                <div className="typing-dot" style={{ background: '#C2A46D' }}></div>
                <div className="typing-dot" style={{ background: '#C2A46D' }}></div>
                <div className="typing-dot" style={{ background: '#C2A46D' }}></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="chat-input-container">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('chat.placeholder')}
              className="chat-input"
              disabled={isLoading}
              data-testid="chat-input"
              style={{ borderColor: inputValue ? '#C2A46D' : '#E8E4DC' }}
            />
            <button 
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
              className="chat-send-btn"
              data-testid="chat-send"
              style={{ background: '#1F2A44' }}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Chat Bubble */}
      <button 
        className="chat-bubble"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-toggle"
        aria-label="Open chat assistant"
        style={{ borderColor: '#C2A46D' }}
      >
        {isOpen ? (
          <div className="w-full h-full flex items-center justify-center" style={{ background: '#1F2A44' }}>
            <X className="w-6 h-6 text-white" />
          </div>
        ) : (
          <img 
            src={AVATAR_URL} 
            alt="Chat with AI Assistant" 
            className="w-full h-full object-cover"
          />
        )}
      </button>
    </div>
  );
};

export default AIChatWidget;
